import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { showInternalServerErrorMessage } from 'common/ToastMessage/utils'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { useGenerateContent } from 'components/Wizards/Wizard/useGenerateContent'
import { useDefaultFolder } from 'hooks/queries/projects/useDefaultFolder'
import { useCurrentFolderStore } from 'hooks/stores/useCurrentFolderStore'
import { useRef } from 'react'
import { generatePath, useHistory } from 'react-router-dom'
import { useRootStore } from 'stores/RootStore'
import { AlmanacFile } from 'types'
import { reportError } from 'utils/error'
import { DOC_PATH } from 'paths'
import { useModals } from 'almanac-ui/Modal'
import { GenerationType } from 'components/BlazeCommandLine/api/createDocGenerationRequest'
import { TextValues } from '../Types'

type HookOptions = {
  navigateToDocOnComplete?: boolean
  loadingToast?: string
}

export const useMobileWizardCreateDoc = (channel: ChannelType | GenerationType, options?: HookOptions) => {
  const { navigateToDocOnComplete = true, loadingToast } = options ?? {}
  const creatingRef = useRef(false)
  const { drawersStore } = useRootStore()
  const { closeAllModals } = useModals()
  const defaultFolder = useDefaultFolder()
  const { currentFolder } = useCurrentFolderStore()
  const history = useHistory()

  const { generateContent } = useGenerateContent({
    generationType: channel,
    navigateToDocOnComplete: false,
  })

  const createDoc = async (values: TextValues) => {
    const {
      filename,
      project,
      topic,
      tone,
      language,
      brand_kit_id,
      brand_voice_id,
      count,
      ideas_count,
      cross_post_accounts = [],
      selected_images = [],
      external_links,
      content_type,
      audience,
      blog_length,
      seo_words,
      include_emojis,
      title,
    } = values

    if (creatingRef.current) {
      return
    }

    creatingRef.current = true

    const generatingToast = loadingToast ? showToastMessage(loadingToast, 'loading', undefined, 0) : undefined

    try {
      const projectId = project?.id || currentFolder?.id || defaultFolder?.id || ''

      const result = await generateContent({
        topic,
        tone,
        language,
        filename,
        project: project || ({ id: projectId } as AlmanacFile),
        brand_kit_id,
        brand_voice_id,
        count,
        ideas_count,
        cross_post_accounts,
        selected_images,
        external_links,
        content_type,
        audience,
        blog_length,
        seo_words,
        include_emojis,
        title,
      })

      if (navigateToDocOnComplete && result && 'almanac_file' in result && result.almanac_file.slug) {
        drawersStore.closeAllDrawers()
        closeAllModals()
        history.push(generatePath(DOC_PATH, { slug: result.almanac_file.slug }))
      }

      return result && 'almanac_file' in result ? result.almanac_file : null
    } catch (error) {
      reportError(error)
      showInternalServerErrorMessage()
      return null
    } finally {
      generatingToast?.()
      creatingRef.current = false
    }
  }

  return {
    isLoading: creatingRef.current,
    createDoc,
  }
}
